/**
 * HTML template for the printable "Scan to view this listing" open-home sheet.
 *
 * Called from OpenHomeQrModal.jsx — the modal grabs an SVG QR rendered off
 * screen and passes the markup in together with the inspection details. The QR
 * points at the property's public buyer-portal listing. Everything is inlined
 * (CSS + SVG) so the popup is self-contained and prints cleanly without
 * external network access.
 */

export function buildOpenHomePrintHtml(vars) {
  const {
    logoUrl,
    agencyName = 'Collings Real Estate',
    propertyAddress = '',
    dateStr = '',
    timeStr = '',
    saleMethodLabel = '',
    portalUrl = '',
    qrSvgMarkup = '',
  } = vars;

  const address = esc(propertyAddress || 'Property listing');

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Open Home — ${address}</title>
<style>
  @page { size: A4; margin: 0; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    color: #1f2937;
    background: #fff;
  }
  .sheet {
    width: 210mm;
    min-height: 297mm;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
  }

  .accent-bar {
    height: 24px;
    background: #4EADB7;
  }
  .header {
    padding: 24px 48px 18px;
    text-align: center;
    background: #fff;
  }
  .logo { height: 56px; display: block; margin: 0 auto; }

  .title {
    padding: 36px 56px 0;
    text-align: center;
  }
  .title h1 {
    font-size: 26px;
    margin: 0 0 6px;
    color: #1f2937;
    font-weight: 700;
  }
  .title p {
    font-size: 14px;
    color: #6B7280;
    margin: 0;
  }

  .meta {
    padding: 28px 56px;
    display: flex;
    justify-content: space-between;
    gap: 24px;
    border-bottom: 1px solid #E5E7EB;
    margin: 0 56px;
    padding: 20px 0;
  }
  .meta-item .label {
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #9CA3AF;
    margin-bottom: 4px;
    font-weight: 600;
  }
  .meta-item .value {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
  }

  .qr-section {
    padding: 36px 56px 24px;
    text-align: center;
  }
  .qr-frame {
    display: inline-block;
    border: 2px solid #4EADB7;
    border-radius: 12px;
    padding: 20px;
    background: #fff;
  }
  .qr-frame svg { width: 280px; height: 280px; display: block; }
  .qr-caption {
    font-size: 13px;
    color: #6B7280;
    margin-top: 14px;
  }

  .steps {
    padding: 8px 56px 24px;
    max-width: 520px;
    margin: 0 auto;
  }
  .steps h2 {
    font-size: 14px;
    color: #4EADB7;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    font-weight: 700;
    margin: 0 0 12px;
    text-align: center;
  }
  .steps ol {
    margin: 0;
    padding-left: 0;
    list-style: none;
    counter-reset: step;
  }
  .steps li {
    counter-increment: step;
    padding-left: 34px;
    position: relative;
    margin-bottom: 10px;
    font-size: 13px;
    color: #374151;
    line-height: 1.55;
  }
  .steps li::before {
    content: counter(step);
    position: absolute;
    left: 0; top: -1px;
    width: 24px; height: 24px;
    background: #4EADB7;
    color: #fff;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .url {
    margin: 0 56px 24px;
    padding: 12px 16px;
    background: #F9FAFB;
    border: 1px dashed #D1D5DB;
    border-radius: 8px;
    font-size: 11px;
    color: #4B5563;
    word-break: break-all;
    text-align: center;
    font-family: 'SF Mono', Menlo, Consolas, monospace;
  }

  .footer {
    margin-top: auto;
    padding: 18px 56px;
    border-top: 1px solid #E5E7EB;
    text-align: center;
    font-size: 11px;
    color: #9CA3AF;
  }
  .footer strong { color: #1f2937; font-weight: 600; }
</style>
</head>
<body>
  <div class="sheet">
    <div class="accent-bar"></div>
    <header class="header">
      <img class="logo" src="${esc(logoUrl)}" alt="${esc(agencyName)}" />
    </header>

    <section class="title">
      <h1>View This Listing</h1>
      <p>Scan the QR code to see photos, details and documents on our portal.</p>
    </section>

    <div class="meta">
      <div class="meta-item">
        <div class="label">Property</div>
        <div class="value">${address}</div>
      </div>
      <div class="meta-item">
        <div class="label">Date</div>
        <div class="value">${esc(dateStr) || '—'}</div>
      </div>
      <div class="meta-item">
        <div class="label">Time</div>
        <div class="value">${esc(timeStr) || '—'}</div>
      </div>
      <div class="meta-item">
        <div class="label">Sale method</div>
        <div class="value">${esc(saleMethodLabel) || '—'}</div>
      </div>
    </div>

    <section class="qr-section">
      <div class="qr-frame">${qrSvgMarkup}</div>
      <div class="qr-caption">Open your phone camera and point it at the QR code.</div>
    </section>

    <section class="steps">
      <h2>How to view</h2>
      <ol>
        <li>Open your phone camera and point it at the QR code above.</li>
        <li>Tap the link that appears.</li>
        <li>Browse the full listing — photos, floorplans and documents.</li>
        <li>Register on the portal to save it, enquire or make an offer.</li>
      </ol>
    </section>

    <div class="url">${esc(portalUrl)}</div>

    <footer class="footer">
      <strong>${esc(agencyName)}</strong> &nbsp;·&nbsp; collings.com.au
    </footer>
  </div>
  <script>window.onafterprint = function(){ setTimeout(function(){ window.close(); }, 100); };</script>
</body>
</html>`;
}

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
